import Control from '@/Controls/Control';
import ComponentManager from '@/Management/ComponentManager';
import LinkViewModelType from '@/ViewModels/Controls/LinkViewModel';
import LabelType from '@/Controls/Label';
import ImageType from '@/Controls/Image';
import OverlayType from '@/Controls/Overlay';
import EventHelper from '@/Utility/EventHelper';
import Utilities from '@/Utility/Utilities';
import {DisposeBehavior} from '#/DefaultViewExtensionInterfaces';

export default class Link extends Control {

    private Label = ComponentManager.getControl<typeof LabelType>('Label');
    private Image = ComponentManager.getControl<typeof ImageType>('Image');
    private Overlay = ComponentManager.getControl<typeof OverlayType>('Overlay');
    protected jQueryControl: JQuery;
    private label: LabelType;
    private image: ImageType;
    private overlay: OverlayType;

    public constructor(protected viewModel: LinkViewModelType, private element?: JQuery) {
        super(viewModel, element);

        this.onWidgetCreated(this.onWidgetCreatedCallback.bind(this));
    }

    public getControl(): JQuery {
        if (this.jQueryControl != null) {
            return this.jQueryControl;
        }

        this.jQueryControl = this.element || $('<a>');
        if (this.viewModel.isExtern) {
            EventHelper.ignoreRADEventHandlers(this.jQueryControl[0]);
            (this.jQueryControl[0] as HTMLAnchorElement).target = '_blank';
        }

        this.widget(null);

        return this.jQueryControl;
    }

    private onWidgetCreatedCallback(widget: null): void {
        this.applyBindings(this.jQueryControl, {
            controlName: 'link',

            attr: {
                href: this.defineComputed(() => {
                    const value = ko.unwrap(this.viewModel.href);
                    if (Utilities.isNullOrUndefined(value) || Utilities.isBlank(value))
                        return null;

                    return value;
                }),

                'aria-disabled': this.defineComputed(() => {
                    return (!this.viewModel.isEnabled()).toString();
                })
            },

            focus: this.defineComputed({
                read: () => {
                    // Toolbar-Links verwalten ihren tabindex selbst.
                    if (this.viewModel.isToolBarLink()) return false;
                    return this.viewModel.isFocusable() && this.viewModel.isEnabled();
                }
            }),

            locator: this.viewModel.locator,

            technicalName: this.viewModel.technicalName,

            loader: this.viewModel.isBusy,

            controlIdent: this.identifier,

            tooltip: this.viewModel.tooltip,

            visible: this.viewModel.isVisible,

            styles: this.definePureComputed({
                read: () => {
                    let styles = [];

                    if (this.viewModel.isToolBarLink()) {
                        styles.push('toolbar');
                    } else {
                        styles.push('default');
                    }

                    return styles;
                }
            }),

            states: this.definePureComputed({
                read: () => {
                    let states = [];

                    if (this.viewModel.isPrimaryLink()) {
                        states.push('primary');
                    }

                    if (this.viewModel.isActive()) {
                        states.push('active');
                    } else {
                        states.push('inactive');
                    }

                    if (this.viewModel.isEnabled()) {
                        states.push('enabled');
                    } else {
                        states.push('disabled');
                    }

                    if (!(this.viewModel.onClick as any).isEmpty) {
                        states.push('hasClickListener');
                    }

                    if (!this.viewModel.isVisible())
                        states.push('hidden');

                    return states;
                }
            }),

            hasFocus: this.viewModel.hasFocus,

            hotkey: this.viewModel.hotkey
        });

        EventHelper.bindClickEvent(this.jQueryControl, (openInNewWindow, event) => {
            if (!this.isDisposed && this.viewModel.isEnabled() && !this.viewModel.isExtern) {
                this.viewModel.onClick(openInNewWindow, event);
            }
        }, this);

        const isToolBarLink = this.viewModel.isToolBarLink();

        if (this.viewModel.image != null) {
            this.image = new this.Image(this.viewModel.image);
            let tuple = this.image.getTuple();

            tuple.control.appendTo(this.jQueryControl);
            // Icon vor Screenreadern verbergen, da der Button ein eigenes aria-label trägt.
            if (isToolBarLink)
                tuple?.control.attr('aria-hidden', 'true');
            this.applyBindings(this.jQueryControl, {
                attr: {
                    role: 'button'
                }
            });
        }
        else
            this.applyBindings(this.jQueryControl, {
                attr: {
                    role: 'link'
                }
            });

        if (this.viewModel.overlay != null) {
            this.overlay = new this.Overlay(this.viewModel.overlay, this.jQueryControl);
            let tuple = this.overlay.getTuple();
        }

        if (this.viewModel.label != null) {
            this.label = new this.Label(this.viewModel.label);
            let tuple = this.label.getTuple();

            tuple.control.appendTo(this.jQueryControl);
            // Text fließt ins aria-label des Toolbar-Buttons ein -> vor dem Screen-Reader verbergen.
            if (isToolBarLink)
                tuple?.control.attr('aria-hidden', 'true');
        }

        // Icon und Label sind für Screenreader per aria-hidden ausgeblendet, damit sie nicht einzeln
        // vorgelesen werden. Stattdessen ein aria-label direkt am Button.
        if (isToolBarLink) {
            this.applyBindings(this.jQueryControl, {
                attr: {
                    'aria-label': this.defineComputed(() => {
                        const labelCaption = ko.unwrap(this.viewModel.label?.caption);
                        const tooltipCaption = ko.unwrap(this.viewModel.tooltip?.caption);
                        const imageAlternativeText = ko.unwrap(this.viewModel.image?.alternativeText);
                        return labelCaption || tooltipCaption || imageAlternativeText || null;
                    })
                }
            });
        } else if (this.viewModel.ariaLabel) {
            // Explizites aria-label überschreibt den vom Label-Text abgeleiteten Accessible Name.
            this.jQueryControl.attr('aria-label', this.viewModel.ariaLabel);
        } else if (this.viewModel.image != null && this.viewModel.label == null) {
            // Accessible name fuer Icon-only Buttons (kein Label vorhanden) (WCAG 4.1.2)
            this.applyBindings(this.jQueryControl, {
                attr: {
                    'aria-label': this.defineComputed(() => {
                        const tooltipCaption = ko.unwrap(this.viewModel.tooltip?.caption);
                        if (tooltipCaption) return tooltipCaption;

                        return this.viewModel.technicalName ?? null;
                    })
                }
            });
        }

        // Sowohl role=button (APG Button Pattern) als auch role=link (APG Link Pattern) werden
        // einheitlich mit Enter und Space aktiviert. <a>-Elemente unterstützen das nativ nicht vollständig.
        this.addEventHandler('keydown', this.jQueryControl, (event: JQuery.KeyDownEvent) => {
            if (!Utilities.isConfirmKey(event.key)) return;
            if (this.isDisposed || !this.viewModel.isEnabled()) return;
            event.preventDefault(); // Space-Scroll der Seite verhindern
            if (!this.viewModel.isExtern) {
                this.viewModel.onClick();
                this.jQueryControl.trigger('click');
            } else {
                // Für externe Links nativen Browser-Click auslösen (<a>-Navigation in neuem Tab).
                this.jQueryControl[0].click();
            }
        });

    }

    protected getDisposeBehavior(): DisposeBehavior {
        return "AsynchronousBatched";
    }
}

ComponentManager.setControl('Link', Link, 'RADPresentation.HTMLClient.DefaultViewExtensions');